import { createFileRoute, Link, useSearch } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { verifyAccountErasure } from "@/lib/deletion.functions";
import { BRAND } from "@/lib/brand";

export const Route = createFileRoute("/account/delete/verify")({
  head: () => ({
    meta: [
      { title: `Confirm deletion — ${BRAND.name}` },
      { name: "description", content: `Confirm the secure link from your email to schedule erasure of your ${BRAND.name} account and personal data.` },
      { name: "robots", content: "noindex,nofollow" },
    ],
    links: [{ rel: "canonical", href: `${BRAND.url}/account/delete/verify` }],
  }),
  validateSearch: z.object({ token: z.string().min(32).max(128).optional() }),
  component: VerifyDeletionPage,
});

function VerifyDeletionPage() {
  const { token } = useSearch({ from: "/account/delete/verify" });
  const verify = useServerFn(verifyAccountErasure);
  const [state, setState] = useState<"loading" | "ok" | "error">("loading");
  const [message, setMessage] = useState("");
  const [scheduledAt, setScheduledAt] = useState<string | null>(null);

  useEffect(() => {
    if (!token) { setState("error"); setMessage("Missing verification token."); return; }
    let active = true;
    (async () => {
      try {
        const r = await verify({ data: { token } });
        if (!active) return;
        if ("error" in r) { setState("error"); setMessage(r.error ?? "Verification failed."); return; }
        setScheduledAt(r.scheduled_deletion_at);
        setState("ok");
      } catch (e) {
        if (!active) return;
        setState("error");
        setMessage(e instanceof Error ? e.message : "Could not verify this link.");
      }
    })();
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  return (
    <div className="min-h-dvh bg-background text-foreground flex flex-col">
      <header className="px-8 py-6 lg:px-16">
        <Link to="/" className="inline-block">
          <p className="tracking-wide-luxury text-accent">The Hospitality</p>
          <p className="font-display text-lg">Reserve</p>
        </Link>
      </header>
      <main className="flex flex-1 items-center justify-center px-6 py-12">
        <div className="w-full max-w-md text-center">
          <p className="tracking-wide-luxury text-accent">Account Deletion</p>
          <span className="gold-divider mt-6" />
          {state === "loading" && (
            <>
              <h1 className="mt-6 font-display text-3xl">Verifying…</h1>
              <p className="mt-3 text-sm text-muted-foreground">One moment please.</p>
            </>
          )}
          {state === "ok" && (
            <>
              <h1 className="mt-6 font-display text-3xl">Confirmed.</h1>
              <p className="mt-3 text-sm text-muted-foreground">
                Your account will be permanently deleted on{" "}
                <strong>{scheduledAt ? new Date(scheduledAt).toLocaleString("en-GB") : "the scheduled date"}</strong>.
                You can cancel any time before then from your Account page.
              </p>
              <Link to="/account" className="mt-8 inline-block border border-border px-6 py-3 text-xs tracking-wide-luxury hover:border-accent">
                Back to Account
              </Link>
            </>
          )}
          {state === "error" && (
            <>
              <h1 className="mt-6 font-display text-3xl">We couldn't confirm this.</h1>
              <p className="mt-3 text-sm text-muted-foreground">{message}</p>
              <Link to="/account" className="mt-8 inline-block border border-border px-6 py-3 text-xs tracking-wide-luxury hover:border-accent">
                Back to Account
              </Link>
            </>
          )}
        </div>
      </main>
    </div>
  );
}
